export type SpeculativeOutcome = "hit" | "miss" | "failed";

/**
 * Starts loading for `guess` right away while `actual` is still resolving.
 * A guess confirmed by `actual` is returned as-is; a wrong or failed guess
 * has its signal aborted and the load restarts for the actual key.
 */
export async function speculativeLoad<K, T>(
  guess: K,
  actual: Promise<K>,
  start: (key: K, signal: AbortSignal) => Promise<T>,
  onOutcome?: (outcome: SpeculativeOutcome, error?: unknown) => void
): Promise<T> {
  const controller = new AbortController();
  let speculation: Promise<T> | null = null;
  let startError: unknown = null;
  try {
    speculation = start(guess, controller.signal);
    // not unhandled: the rejection resurfaces when awaited below
    speculation.catch(() => void 0);
  } catch (error) {
    startError = error;
  }

  let key: K;
  try {
    key = await actual;
  } catch (error) {
    controller.abort();
    throw error;
  }

  if (key !== guess) {
    controller.abort();
    onOutcome?.("miss");
    return start(key, new AbortController().signal);
  }

  let error = startError;
  if (speculation) {
    try {
      const result = await speculation;
      onOutcome?.("hit");
      return result;
    } catch (e) {
      error = e;
      controller.abort();
    }
  }

  onOutcome?.("failed", error);
  return start(key, new AbortController().signal);
}
